import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogContent,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import CheckIcon from "@mui/icons-material/Check";
import { useSelector } from "react-redux";
import {
  getAllBills,
  getBillMetrics,
  downloadBill,
} from "../../../Restclient/datacollection";
import CustomActionButton from "../../CommonComponents/CustomActionButton";
import "../../../cssfiles/billing.css";
import SearchIcon from "../../../images/LK_Search";

const BillViewer = ({ bill, setBills, theme }) => {
  const [viewBill, setViewBill] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState(null);

  const handleView = () => {
    setLoading(true);
    setError(null);

    downloadBill(bill.id, (result) => {
      if (result && !(result.state === false)) {
        const blob = new Blob([result], { type: "application/pdf" });
        const url1 = URL.createObjectURL(blob);
        setPdfUrl(url1);
        setViewBill(true);
      } else {
        setError(result?.message || "Failed to load bill PDF");
      }
      setLoading(false);
    });
  };

  const handleClose = () => {
    setViewBill(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  return (
    <>
      <CustomActionButton
        buttonText={"View"}
        onClick={handleView}
        disabled={loading}
      />

      <Dialog
        maxWidth="lg"
        fullWidth
        open={viewBill}
        onClose={handleClose}
        PaperProps={{
          className: `bill-dialog-paper bill-dialog-paper--${theme}`,
        }}
        BackdropProps={{
          className: `bill-dialog-backdrop--${theme}`,
        }}
      >
        <DialogContent>
          <div className="bill-dialog-header">
            <Typography
              className={`bill-dialog-title bill-dialog-title--${theme}`}
            >
              Bill Details
            </Typography>
            <div className="bill-dialog-spacer" />
          </div>
          <div className="bill-pdf-container">
            {loading ? (
              <div className="bill-status-container">
                <Typography>Loading bill PDF...</Typography>
              </div>
            ) : error ? (
              <div className="bill-error-container">
                <Typography>Error: {error}</Typography>
              </div>
            ) : pdfUrl ? (
              <iframe
                className="bill-pdf-object"
                src={pdfUrl}
                type="application/pdf"
                width="1000px"
                aria-label="PDF file"
              ></iframe>
            ) : (
              <div className="bill-status-container">
                <Typography>No PDF available</Typography>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const BillingMetrics = ({ metrics }) => {
  const metricsData = [
    {
      label: "Total Bills",
      value: metrics.totalCustomers || 0,
      color: "var(--PrimaryText)",
    },
    {
      label: "Maximum Energy Consumed",
      value: metrics.maxEnergyConsumed
        ? `${metrics.maxEnergyConsumed} kWh`
        : "0 kWh",
      color: "var(--PrimaryText)",
    },
    {
      label: "Minimum Energy Consumed",
      value: metrics.minEnergyConsumed
        ? `${metrics.minEnergyConsumed} kWh`
        : "0 kWh",
      color: "var(--PrimaryText)",
    },
  ];

  return (
    <div className="billing-metrics-card card2">
      <div className="billing-metrics-container">
        {metricsData.map((metric, index) => (
          <React.Fragment key={index}>
            <div className="billing-metric-item cardtext">
              <div>
                <Typography className="billing-metric-label">
                  {metric.label}
                </Typography>
              </div>
              <div>
                <Typography
                  className="billing-metric-value"
                  style={{ color: metric.color }}
                >
                  {metric.value}
                </Typography>
              </div>
            </div>
            {index < metricsData.length - 1 && (
              <Divider
                orientation="vertical"
                variant="middle"
                className="billing-metric-divider"
                flexItem
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default function BillingDashboard(props) {
  const themechange = useSelector((state) => state.themedata.theme);
  const [theme, setTheme] = useState("light");
  const [openForm, setOpenForm] = useState(false);
  const [bills, setBills] = useState([]);
  const [search, setSearch] = useState({ searchText: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [searchList, setSearchList] = useState([]); // Add this
  const [billingPeriodMenuAnchor, setBillingPeriodMenuAnchor] = useState(null);
  const [metrics, setMetrics] = useState({
    totalCustomers: 0,
    pendingPayments: 0,
    overdue: 0,
    paidThisMonth: 0,
  });
  const [yearsMenuAnchor, setYearsMenuAnchor] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [monthsMenuAnchor, setMonthsMenuAnchor] = useState(null);

  useEffect(() => {
    setTheme(themechange);
  }, [themechange]);

  const fetchBills = useCallback(() => {
    if (bills.length > 0) return;
    setLoading(true);
    setError(null);
    getAllBills((billsResponse) => {
      if (!billsResponse.state) {
        setError(billsResponse.message || "Failed to fetch bills");
        setBills([]);
        setLoading(false);
        return;
      }

      Promise.all(
        billsResponse.bills.map(async (bill) => {
          let energySum = 0;

          return {
            id: bill.id,
            billId: bill.billId,
            billNumber: bill.id.substring(0, 8),
            customerId: bill.customerId,
            customer: bill.customerName || "Unknown Customer",
            customerName: bill.customerName || "Unknown Customer",
            address: bill.address || "N/A",
            consumerNumber: bill.consumerNumber || "N/A",
            contact: bill.contact || "N/A",
            email: bill.email || "N/A",
            serialNumber: bill.serialNumber,
            meterId: bill.meterId || "N/A",
            meterStatus: bill.meterStatus || "Unknown",
            tariffId: bill.tariffId || "N/A",
            tariffCategory: bill.tariffCategory || "N/A",
            sanctionedLoad: bill.sanctionedLoad || "N/A",
            supplyDate: bill.supplyDate
              ? new Date(bill.supplyDate).toLocaleDateString("en-IN")
              : "N/A",
            previousReading: bill.previousReading || 0,
            currentReading: bill.currentReading || 0,
            unitsConsumed: bill.unitsConsumed || 0,
            billingPeriodStart: bill.billingPeriodStart
              ? new Date(bill.billingPeriodStart).toLocaleDateString("en-IN")
              : "N/A",
            billingPeriodEnd: bill.billingPeriodEnd
              ? new Date(bill.billingPeriodEnd).toLocaleDateString("en-IN")
              : "N/A",
            billingPeriod:
              bill.billingPeriodStart && bill.billingPeriodEnd
                ? `${new Date(bill.billingPeriodStart).toLocaleDateString(
                    "en-IN",
                  )} - ${new Date(bill.billingPeriodEnd).toLocaleDateString(
                    "en-IN",
                  )}`
                : "N/A",
            billingMonth: bill.billingPeriodStart
              ? new Date(bill.billingPeriodStart).toISOString().slice(0, 7)
              : "",
            fixedCharges: bill.fixedCharges || 0,
            energyCharges: bill.energyCharges || 0,
            wheelingCharges: bill.wheelingCharges || 0,
            fac: bill.fac || 0,
            electricityDuty: bill.electricityDuty || 0,
            currentInterest: bill.currentInterest || 0,
            otherCharges: bill.otherCharges || 0,
            totalBill: bill.totalBill,
            amount: `Rs. ${bill.totalBill || "0.00"}`,
            regularPayment: bill.regularPayment || 0,
            latePayment: bill.latePayment || 0,
            dueDate: bill.dueDate
              ? new Date(bill.dueDate).toLocaleDateString("en-IN")
              : "N/A",
            generatedDate: bill.generatedDate
              ? new Date(bill.generatedDate).toLocaleDateString("en-IN")
              : "N/A",
            status: bill.status || "UNKNOWN",
            type: bill.type || "BILL",
          };
        }),
      ).then((formattedBills) => {
        const totalCount = new Set(
          formattedBills.map((item) => item.customerId),
        );

        const energyConsumptionValues = formattedBills
          .map((bill) => bill.unitsConsumed || 0)
          .filter((value) => value > 0);

        const maxEnergyConsumed = energyConsumptionValues.length
          ? Math.max(...energyConsumptionValues)
          : 0;

        const minEnergyConsumed = energyConsumptionValues.length
          ? Math.min(...energyConsumptionValues)
          : 0;

        const avgEnergyConsumption = energyConsumptionValues.length
          ? energyConsumptionValues.reduce((sum, value) => sum + value, 0) /
            energyConsumptionValues.length
          : 0;

        setBills(formattedBills);
        setMetrics({
          totalCustomers: totalCount.size,
          maxEnergyConsumed,
          minEnergyConsumed,
          avgEnergyConsumption,
        });

        getBillMetrics((metricsResponse) => {
          metricsResponse = metricsResponse.data;
          if (metricsResponse?.state) {
            setMetrics((prevMetrics) => ({
              ...prevMetrics,
              pendingPayments:
                metricsResponse.metrics?.statusDistribution?.Pending,
              overdue: metricsResponse.metrics?.statusDistribution?.Overdue,
              paidThisMonth: metricsResponse.metrics?.statusDistribution?.Paid,
              maxEnergyConsumed:
                metricsResponse.metrics?.maxEnergyConsumed ||
                prevMetrics.maxEnergyConsumed,
              minEnergyConsumed:
                metricsResponse.metrics?.minEnergyConsumed ||
                prevMetrics.minEnergyConsumed,
              avgEnergyConsumption:
                metricsResponse.metrics?.avgEnergyConsumption ||
                prevMetrics.avgEnergyConsumption,
            }));
          }
          setLoading(false);
        });
      });
    });
  }, [bills]);

  useEffect(() => {
    fetchBills();

    const pollInterval = setInterval(() => {
      fetchBills(true);
    }, 60000);

    return () => clearInterval(pollInterval);
  }, [fetchBills]);

  const getYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 11; i++) {
      years.push(currentYear - i);
    }
    return years;
  };

  const getMonthNames = () => {
    return [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
  };

  const handleBillingPeriodFilterClick = (event) => {
    setBillingPeriodMenuAnchor(event.currentTarget);
  };

  const handleYearMenuClick = (event) => {
    setYearsMenuAnchor(event.currentTarget);
  };

  const handleYearSelect = (year) => {
    setSelectedYear(year);
    setYearsMenuAnchor(null);
  };

  const handleMonthsMenuOpen = (event) => {
    setMonthsMenuAnchor(event.currentTarget);
  };

  const handleMonthSelect = (monthIndex) => {
    const month = String(monthIndex + 1).padStart(2, "0");
    const selectedMonthValue = `${selectedYear}-${month}`;

    setSelectedMonth(selectedMonthValue);
    setMonthsMenuAnchor(null);
    setBillingPeriodMenuAnchor(null);

    setTimeout(() => {
      handleSearchClick();
    }, 100);
  };

  const clearMonthFilter = () => {
    setSelectedMonth("");
    setSelectedYear(null);
    setBillingPeriodMenuAnchor(null);

    setTimeout(() => {
      handleSearchClick();
    }, 100);
  };

  const handleBillingPeriodMenuClose = () => {
    setBillingPeriodMenuAnchor(null);
    setYearsMenuAnchor(null);
    setMonthsMenuAnchor(null);
  };

  const handleSearchClick = async () => {
    try {
      setIsLoading(true);

      // Filter from the original bills array, not fetching again
      const filteredBills = bills.filter((bill) => {
        const searchLower = search.searchText.toLowerCase().trim();

        const matchesSearch =
          !searchLower ||
          (bill.customerName &&
            bill.customerName.toLowerCase().includes(searchLower)) ||
          (bill.customer &&
            bill.customer.toLowerCase().includes(searchLower)) ||
          (bill.consumerNumber &&
            bill.consumerNumber.toLowerCase().includes(searchLower)) ||
          (bill.id && bill.id.toLowerCase().includes(searchLower));

        const matchesMonth =
          !selectedMonth ||
          (bill.billingMonth && bill.billingMonth === selectedMonth);

        return matchesSearch && matchesMonth;
      });

      setSearchList(filteredBills);
      setIsSearching(true);
    } catch (err) {
      console.error("Search error:", err);
      setError(err.message || "Failed to search bills");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchCancel = () => {
    setSearch({ searchText: "", filterSelected: "" });
    setSelectedMonth("");
    setSelectedYear(null);
    setSearchList([]);
    setIsSearching(false);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearchClick();
    }
  };

  const displayBills = isSearching ? searchList : bills;

  return (
    <Box
      className={
        openForm ? "billing-dashboard-form-open" : "billing-dashboard-container"
      }
    >
      <>
        <BillingMetrics metrics={metrics} />

        <Box className="header-container">
          <Box></Box>
          <Box className="search-actions-container">
            <TextField
              size="small"
              placeholder="Search by Customer Name "
              variant="standard"
              value={search.searchText}
              onChange={(e) =>
                setSearch({ ...search, searchText: e.target.value })
              }
              onKeyPress={handleKeyPress}
              InputProps={{
                endAdornment: search.searchText ? (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={handleSearchCancel}
                      size="small"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              className={`search-field search-field--${themechange}`}
            />
            <IconButton
              onClick={handleSearchClick}
              style={{
                fontSize: "12px",
                width: "30px",
                minWidth: "30px",
                fontWeight: 400,
                color: "var(--Searchtext_muted)",
                fontFamily: "Roboto",
                textAlign: "left",
                justifySelf: "right",
                cursor: "pointer",
                textTransform: "none",
                marginLeft: "0px",
                padding: 0,
              }}
            >
              <SearchIcon
                color={theme === "light" ? "#3C3C3C" : "#C1C1C1"}
                width={30}
                height={30}
              />
            </IconButton>
          </Box>
        </Box>

        <TableContainer
          component={Paper}
          className="table-container"
          elevation={0}
        >
          <Table stickyHeader className="customers-table" size="small">
            <TableHead>
              <TableRow className="table-header-row">
                <TableCell
                  className="table-header-cell"
                  sx={{
                    fontSize:
                      props.widgetprop.props.extended.tablehead.fontsize,
                    fontWeight:
                      props.widgetprop.props.extended.tablehead.fontweight,
                  }}
                >
                  Customer
                </TableCell>
                <TableCell
                  className="table-header-cell"
                  sx={{
                    fontSize:
                      props.widgetprop.props.extended.tablehead.fontsize,
                    fontWeight:
                      props.widgetprop.props.extended.tablehead.fontweight,
                  }}
                >
                  Amount
                </TableCell>
                <TableCell
                  className="table-header-cell"
                  sx={{
                    fontSize:
                      props.widgetprop.props.extended.tablehead.fontsize,
                    fontWeight:
                      props.widgetprop.props.extended.tablehead.fontweight,
                  }}
                >
                  Due Date
                </TableCell>
                <TableCell
                  className="table-header-cell billing-period-header"
                  sx={{
                    fontSize:
                      props.widgetprop.props.extended.tablehead.fontsize,
                    fontWeight:
                      props.widgetprop.props.extended.tablehead.fontweight,
                  }}
                >
                  Billing Period
                  <IconButton
                    size="small"
                    onClick={handleBillingPeriodFilterClick}
                    className="filter-icon-button"
                    id="billing-period-filter-button"
                    aria-label="Filter billing period"
                  >
                    <FilterListIcon
                      fontSize="small"
                      className={selectedMonth ? "filter-icon-active" : ""}
                    />
                  </IconButton>
                  <Menu
                    id="billing-period-menu"
                    anchorEl={billingPeriodMenuAnchor}
                    open={Boolean(billingPeriodMenuAnchor)}
                    onClose={handleBillingPeriodMenuClose}
                  >
                    <MenuItem
                      onClick={clearMonthFilter}
                      className={
                        selectedMonth === "" ? "selected-menu-item" : ""
                      }
                    >
                      <Box className="menu-item-content">
                        {selectedMonth === "" && (
                          <CheckIcon
                            fontSize="small"
                            className="menu-check-icon"
                          />
                        )}
                        All Months
                      </Box>
                    </MenuItem>

                    <Divider className="menu-divider-spacing" />

                    <MenuItem
                      id="years-menu-button"
                      onClick={handleYearMenuClick}
                    >
                      <Box className="submenu-item">
                        <span>
                          {selectedYear
                            ? `Year: ${selectedYear}`
                            : "Select Year"}
                        </span>
                        <ArrowRightIcon fontSize="small" />
                      </Box>
                    </MenuItem>

                    {selectedYear && (
                      <MenuItem
                        id="months-menu-button"
                        onClick={handleMonthsMenuOpen}
                      >
                        <Box className="submenu-item">
                          <span>
                            {selectedMonth
                              ? `Month: ${
                                  getMonthNames()[
                                    parseInt(selectedMonth.split("-")[1], 10) -
                                      1
                                  ]
                                }`
                              : "Select Month"}
                          </span>
                          <ArrowRightIcon fontSize="small" />
                        </Box>
                      </MenuItem>
                    )}

                    {selectedMonth && (
                      <Box className="filter-status">
                        <FilterListIcon
                          fontSize="small"
                          className="filter-status-icon"
                        />
                        <Typography variant="body2">
                          {`Filtering: ${
                            getMonthNames()[
                              parseInt(selectedMonth.split("-")[1], 10) - 1
                            ]
                          } ${selectedMonth.split("-")[0]}`}
                        </Typography>
                      </Box>
                    )}
                  </Menu>
                  <Menu
                    id="years-menu"
                    anchorEl={yearsMenuAnchor}
                    open={Boolean(yearsMenuAnchor)}
                    onClose={() => setYearsMenuAnchor(null)}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                  >
                    {getYears().map((year) => (
                      <MenuItem
                        key={year}
                        onClick={() => handleYearSelect(year)}
                        className={
                          selectedYear === year ? "selected-menu-item" : ""
                        }
                      >
                        <Box className="menu-item-content">
                          {selectedYear === year && (
                            <CheckIcon
                              fontSize="small"
                              className="menu-check-icon"
                            />
                          )}
                          {year}
                        </Box>
                      </MenuItem>
                    ))}
                  </Menu>
                  <Menu
                    id="months-menu"
                    anchorEl={monthsMenuAnchor}
                    open={Boolean(monthsMenuAnchor)}
                    onClose={() => setMonthsMenuAnchor(null)}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                  >
                    {getMonthNames().map((month, index) => {
                      const monthStr = String(index + 1).padStart(2, "0");
                      const monthYearStr = `${selectedYear}-${monthStr}`;

                      return (
                        <MenuItem
                          key={month}
                          onClick={() => handleMonthSelect(index)}
                          className={
                            selectedMonth === monthYearStr
                              ? "selected-menu-item"
                              : ""
                          }
                        >
                          <Box className="menu-item-content">
                            {selectedMonth === monthYearStr && (
                              <CheckIcon
                                fontSize="small"
                                className="menu-check-icon"
                              />
                            )}
                            {month}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </TableCell>

                <TableCell
                  className="table-header-cell"
                  sx={{
                    fontSize:
                      props.widgetprop.props.extended.tablehead.fontsize,
                    fontWeight:
                      props.widgetprop.props.extended.tablehead.fontweight,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" className="error-cell">
                    {error}
                    <Box className="retry-button-container">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          fetchBills().finally(() => setLoading(false))
                        }
                      >
                        Retry
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : bills.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No bills found
                  </TableCell>
                </TableRow>
              ) : (
                bills.map((bill) => (
                  <TableRow key={bill.id} className="billing-table-row">
                    <TableCell
                      component="th"
                      scope="row"
                      className="table-body-cell"
                      sx={{
                        fontSize:
                          props.widgetprop.props.extended.tablerows.fontsize,
                        fontWeight:
                          props.widgetprop.props.extended.tablerows.fontweight,
                      }}
                    >
                      {bill?.customer}
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      className="table-body-cell"
                      sx={{
                        fontSize:
                          props.widgetprop.props.extended.tablerows.fontsize,
                        fontWeight:
                          props.widgetprop.props.extended.tablerows.fontweight,
                      }}
                    >
                      {bill.totalBill}
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      className="table-body-cell"
                      sx={{
                        fontSize:
                          props.widgetprop.props.extended.tablerows.fontsize,
                        fontWeight:
                          props.widgetprop.props.extended.tablerows.fontweight,
                      }}
                    >
                      {bill.dueDate}
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      className="table-body-cell"
                      sx={{
                        fontSize:
                          props.widgetprop.props.extended.tablerows.fontsize,
                        fontWeight:
                          props.widgetprop.props.extended.tablerows.fontweight,
                      }}
                    >
                      {bill.billingPeriod}
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      className="table-body-cell"
                      sx={{
                        fontSize:
                          props.widgetprop.props.extended.tablerows.fontsize,
                        fontWeight:
                          props.widgetprop.props.extended.tablerows.fontweight,
                      }}
                    >
                      <BillViewer
                        bill={bill}
                        setBills={setBills}
                        theme={theme}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    </Box>
  );
}
